/**
 * PKCE (Proof Key for Code Exchange — RFC 7636) helpers.
 *
 * Used by `lib/auth.ts` to drive the OAuth code flow from the SPA without
 * relying on Supabase SSR cookies. Identical implementation to the Phase 0
 * PoC; promoted here as the canonical version for Phase 2.
 */

const VERIFIER_LENGTH = 64;
const VERIFIER_CHARS =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

export function generateCodeVerifier(length: number = VERIFIER_LENGTH): string {
  if (length < 43 || length > 128) {
    throw new RangeError('PKCE verifier length must be 43-128 (RFC 7636)');
  }
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let out = '';
  for (const byte of bytes) {
    out += VERIFIER_CHARS[byte % VERIFIER_CHARS.length];
  }
  return out;
}

export async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
